// Reading cookies. A leaf module — it imports nothing — so anything can use it, including the OneTrust consent
// code that cookie_writer.js depends on. Writing is cookie_writer.js.

/**
 * Split one `name=value` pair of document.cookie at the FIRST `=` only. A value may itself contain `=`
 * (base64 padding, or OneTrust's `OptanonConsent` which is `isGpcEnabled=0&datestamp=…&groups=…`), and a
 * name never does. String#split('=', 2) would not do: its limit drops the extra pieces instead of keeping
 * them in the value. A pair with no `=` has a name and no value, as before.
 *
 * @param {String} cookie one pair, e.g. `a=b=c`
 * @returns {Array} `[name, value]`, or `[name]` when there is no `=`
 */
const parseCookie = cookie => {
  const pair = cookie.trim();
  const separator = pair.indexOf('=');

  return separator === -1 ? [pair] : [pair.slice(0, separator), pair.slice(separator + 1)];
};

// The last jar that was parsed, and its result. See getAllCookies().
let lastJar = null;
let lastCookies = null;

/**
 * Gets all the cookies as a Map
 *
 * The parse is remembered, but keyed on the raw `document.cookie` string, never on time: whatever changes the
 * jar — our own setCookie(), OneTrust, another script, the server, an expiry — changes that string, so the next
 * call parses afresh and a stale answer is impossible. Unchanged, it returns the same Map as last time, which
 * costs a string comparison instead of a parse (the readers run several times per page: the consent checks, the
 * frequency cap, the identity lookup). What it cannot save is reading `document.cookie` itself.
 *
 * Because the Map is shared, treat it as read-only.
 *
 * @returns Map of cookie values
 */
export const getAllCookies = () => {
  const jar = document.cookie;

  if (jar !== lastJar || lastCookies === null) {
    lastCookies = new Map(jar.split(';').map(parseCookie));
    lastJar = jar;
  }

  return lastCookies;
};

/**
 * Checks if cookie exists
 *
 * @param {String} name name of cookie
 * @return {Boolean} true of cookie is present
 */
export const hasCookie = name => {
  return getAllCookies().has(name);
};

/**
 * Gets the cookie value
 *
 * @param {String} name name of cookie
 * @return {String} the value of the cookie
 */
export const getCookie = name => {
  return getAllCookies().get(name);
};
